import qs from 'qs';

import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { Transaction } from 'dashboard/models/transaction';
import { dashboardApi, LIST_TAG_ID } from 'dashboard/redux/api/api';
import { ApiOptions } from 'lib/clients/ApiClient';
import {
  ApiEndpoint,
  ApiListResponse,
  ApiModel,
  ApiResponse,
  PaginationParams,
} from 'lib/interfaces/api';
import { JsonSchema } from 'lib/json-schema';

interface CreateTransactionValues {
  transactionTypeId: string;
  transactionStatusId: string;
  referenceId: string;
  fields: Record<string, any>;
}

export const transactionApi = dashboardApi.injectEndpoints({
  endpoints: (builder) => ({
    createTransaction: builder.mutation<ApiResponse<Transaction>, CreateTransactionValues>({
      query: async ({ transactionTypeId, transactionStatusId, referenceId, fields }) => {
        const payload = {
          attributes: {
            transaction_type_id: transactionTypeId,
            transaction_status_id: transactionStatusId,
            reference_id: referenceId,
            fields,
          },
          type: ApiModel.Transaction as const,
        };
        return SessionClientDashboard.postResourceCreate<Transaction>(payload);
      },
      invalidatesTags: (_result, _error) => {
        return [{ type: ApiModel.Transaction, id: LIST_TAG_ID }];
      },
    }),
    updateTransactionTags: builder.mutation<
      ApiResponse<Transaction>,
      { id: string } & FormUpdateTagsData
    >({
      query: async ({ id, tags }) => {
        return SessionClientDashboard.postResourceAction<Transaction>(
          { type: ApiModel.Transaction, id },
          'set-tags',
          {
            meta: { 'tag-id': tags },
          },
        );
      },
      invalidatesTags: (_result, _error, { id }) => {
        return [{ type: ApiModel.Transaction, id }];
      },
    }),
    updateTransactionStatus: builder.mutation<
      ApiResponse<Transaction>,
      { id: string; transactionStatusId: string }
    >({
      query: async ({ id, transactionStatusId }) => {
        return SessionClientDashboard.updateResource<Transaction>(
          {
            id,
            type: ApiModel.Transaction,
          },
          { meta: { 'transaction-status-id': transactionStatusId } },
        );
      },
      invalidatesTags: (_result, _error, { id }) => {
        return [
          { type: ApiModel.Transaction, id },
          { type: ApiModel.Transaction, id: LIST_TAG_ID },
        ];
      },
    }),
    redactTransaction: builder.mutation<ApiResponse<Transaction>, string>({
      query: async (transactionId) => {
        return SessionClientDashboard.deleteResource<Transaction>(
          ApiEndpoint.Transaction,
          transactionId,
        );
      },
      invalidatesTags: (_result, _error, id) => {
        return [{ type: ApiModel.Transaction, id }];
      },
    }),
    getTransaction: builder.query<ApiResponse<Transaction>, string>({
      query: (id) => SessionClientDashboard.getResource<Transaction>(ApiEndpoint.Transaction, id),
      providesTags: (_result, _error, id) => {
        return [{ type: ApiModel.Transaction, id }];
      },
    }),
    listTransactions: builder.query<ApiListResponse<Transaction>, ApiOptions>({
      query: (options) =>
        SessionClientDashboard.listResource<Transaction, PaginationParams>(
          ApiEndpoint.Transaction,
          options,
        ),
      providesTags: (_result, _error) => {
        return [{ type: ApiModel.Transaction, id: LIST_TAG_ID }];
      },
    }),
    resolveVariable: builder.query<
      unknown,
      { transactionToken: string; variableDefinitionToken: string }
    >({
      query: async ({ transactionToken, variableDefinitionToken }) =>
        SessionClientDashboard.get<JsonSchema>(
          ApiEndpoint.Account,
          transactionToken,
          `resolve-variable?${qs.stringify({ 'variable-definition-token': variableDefinitionToken })}`,
        ),
    }),
  }),
});

// Hooks are autogenerated based on the defined endpoints
export const {
  useCreateTransactionMutation,
  useUpdateTransactionTagsMutation,
  useUpdateTransactionStatusMutation,
  useRedactTransactionMutation,
  useGetTransactionQuery,
  useListTransactionsQuery,
  useLazyResolveVariableQuery,
} = transactionApi;
